import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Stethoscope, Wrench, ShieldCheck, Headphones, Cog, Phone, Mail } from "lucide-react";
import { useI18n } from "@/i18n";
import { IMAGES } from "@/lib/media";

const PILLARS = [
  { key: "pillar1", icon: Stethoscope },
  { key: "pillar2", icon: Wrench },
  { key: "pillar3", icon: ShieldCheck },
  { key: "pillar5", icon: Headphones },
  { key: "pillar6", icon: Cog },
];

export default function ServiceInformatique() {
  const { t } = useI18n();

  return (
    <div data-testid="page-service-informatique">
      {/* HERO — slate accent for IT */}
      <section className="relative overflow-hidden border-b border-stone-200 bg-[#0f1720] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#E8CDB5] font-mono">{t("info.hero.eyebrow")}</div>
            <h1 className="mt-4 font-serif text-6xl md:text-8xl tracking-tight leading-[0.9]">{t("info.hero.title")}</h1>
            <p className="mt-6 text-lg text-stone-300 max-w-xl">{t("info.hero.subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="info-hero-cta" className="btn-primary group">
                {t("hero.cta_primary")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="tel:+15146250359" data-testid="info-hero-phone" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white text-sm hover:bg-white hover:text-stone-900 transition-colors">
                <Phone size={14} /> 514-625-0359
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-lg overflow-hidden border border-white/10">
              <img src={IMAGES.service_it} alt="Informatique" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-3xl mb-14">
          <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono">interventions</div>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-stone-900 leading-tight">{t("info.pillars.title")}</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.key} data-testid={`info-pillar-${p.key}`} className="p-8 rounded-lg border border-stone-200 bg-white hover:-translate-y-1 hover:shadow-lg transition-transform duration-300">
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-md bg-[#293845]/10 flex items-center justify-center text-[#293845]">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] font-mono text-stone-400">0{i + 1}</span>
                </div>
                <h3 className="mt-5 font-serif text-2xl text-stone-900">{t(`info.${p.key}.title`)}</h3>
                <p className="mt-3 text-stone-600 leading-relaxed text-sm">{t(`info.${p.key}.desc`)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="rounded-2xl bg-[#0f1720] text-white p-10 md:p-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <h3 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">{t("info.cta.title")}</h3>
            <p className="mt-4 text-stone-300 text-lg max-w-lg">{t("info.cta.body")}</p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-4">
            <Link to="/contact" data-testid="info-cta-quote" className="btn-primary justify-center">
              {t("hero.cta_primary")} <ArrowRight size={16} />
            </Link>
            <a href="tel:+15146250359" data-testid="info-cta-phone" className="flex items-center gap-3 text-stone-300 hover:text-white transition-colors">
              <Phone size={16} className="text-[#E8CDB5]" /> 514-625-0359
            </a>
            <a href="mailto:gsmservicedemaintenance@gmail.com" data-testid="info-cta-email" className="flex items-center gap-3 text-stone-300 hover:text-white transition-colors break-all">
              <Mail size={16} className="text-[#E8CDB5]" /> gsmservicedemaintenance@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
